import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";

// ─── EAGER-LOADED — first visit, marketing surface ──────────
import LandingPage from "@/pages/LandingPage";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import PricingPage from "@/pages/public/PricingPage";
import PublicPageLayout from "@/components/landing/PublicPageLayout";

// ─── LAZY-LOADED — admin CRM bundle ─────────────────────────
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const DashboardHome = lazy(() => import("@/pages/admin/DashboardHome"));
const PendingMedicalReviews = lazy(() => import("@/pages/admin/PendingMedicalReviews"));
const AdminSignaturePad = lazy(() => import("@/pages/admin/AdminSignaturePad"));
const AdminSecurity = lazy(() => import("@/pages/admin/AdminSecurity"));
const ApplicantList = lazy(() => import("@/pages/admin/ApplicantList"));
const ApplicantProfile = lazy(() => import("@/pages/admin/ApplicantProfile"));
const FulfillmentBoard = lazy(() => import("@/pages/admin/FulfillmentBoard"));
const PackageManager = lazy(() => import("@/pages/admin/PackageManager"));
const AppointmentsPage = lazy(() => import("@/pages/admin/AppointmentsPage"));
const BillingPage = lazy(() => import("@/pages/admin/BillingPage"));
const ReportingPage = lazy(() => import("@/pages/admin/ReportingPage"));
const UserManagement = lazy(() => import("@/pages/admin/UserManagement"));
const LabTracker = lazy(() => import("@/pages/admin/LabTracker"));
const StatusSettings = lazy(() => import("@/pages/admin/StatusSettings"));
const PricingSettings = lazy(() => import("@/pages/admin/PricingSettings"));
const KanbanBoard = lazy(() => import("@/pages/admin/KanbanBoard"));
const ContentHub = lazy(() => import("@/pages/admin/ContentHub"));
const ArticleEditor = lazy(() => import("@/pages/admin/ArticleEditor"));
const CitationLibrary = lazy(() => import("@/pages/admin/CitationLibrary"));
const InventoryPNL = lazy(() => import("@/pages/admin/InventoryPNL"));
const CommunicationsHub = lazy(() => import("@/pages/admin/CommunicationsHub"));
const IntakeTemplatesPage = lazy(() => import("@/pages/admin/IntakeTemplatesPage"));
const PhoneNumbersPage = lazy(() => import("@/pages/admin/PhoneNumbersPage"));
const PayingClientsPage = lazy(() => import("@/pages/admin/PayingClientsPage"));
const SMSAutomationsPage = lazy(() => import("@/pages/admin/SMSAutomationsPage"));

// ─── LAZY-LOADED — patient portal bundle ────────────────────
const PatientLayout = lazy(() => import("@/pages/patient/PatientLayout"));
const PatientDashboard = lazy(() => import("@/pages/patient/PatientDashboard"));
const PatientProgram = lazy(() => import("@/pages/patient/PatientProgram"));
const PatientManagePlan = lazy(() => import("@/pages/patient/PatientManagePlan"));
const PatientNotifications = lazy(() => import("@/pages/patient/PatientNotifications"));
const PatientReferrals = lazy(() => import("@/pages/patient/PatientReferrals"));
const PatientPrescriptions = lazy(() => import("@/pages/patient/PatientPrescriptions"));
const PatientDataExport = lazy(() => import("@/pages/patient/PatientDataExport"));
const PatientSchedule = lazy(() => import("@/pages/patient/PatientSchedule"));
const PatientDoses = lazy(() => import("@/pages/patient/PatientDoses"));
const PatientLabs = lazy(() => import("@/pages/patient/PatientLabs"));
const PatientMessages = lazy(() => import("@/pages/patient/PatientMessages"));
const PatientCallRequest = lazy(() => import("@/pages/patient/PatientCallRequest"));
const PatientNotes = lazy(() => import("@/pages/patient/PatientNotes"));
const PeptideBreakdown = lazy(() => import("@/pages/patient/PeptideBreakdown"));
const PatientCalendar = lazy(() => import("@/pages/patient/PatientCalendar"));
const PatientSurvey = lazy(() => import("@/pages/patient/PatientSurvey"));
const PatientResults = lazy(() => import("@/pages/patient/PatientResults"));
const PatientFiles = lazy(() => import("@/pages/patient/PatientFiles"));
const PatientUrgentCallSuccess = lazy(() => import("@/pages/patient/PatientUrgentCallSuccess"));
const PatientSettings = lazy(() => import("@/pages/patient/PatientSettings"));

// ─── LAZY-LOADED — long-tail public pages ───────────────────
const BlogIndex = lazy(() => import("@/pages/blog/BlogIndex"));
const BlogArticle = lazy(() => import("@/pages/blog/BlogArticle"));
const PublicIntakeForm = lazy(() => import("@/pages/public/PublicIntakeForm"));
const GetStartedLanding = lazy(() => import("@/pages/public/GetStartedLanding"));
const PublicSignup = lazy(() => import("@/pages/public/PublicSignup"));
const PublicDocumentUpload = lazy(() => import("@/pages/public/PublicDocumentUpload"));
const PeptideCategoryPage = lazy(() => import("@/pages/public/PeptideCategoryPage"));
const AboutPage = lazy(() => import("@/pages/public/AboutPage"));
const PhysiciansPage = lazy(() => import("@/pages/public/PhysiciansPage"));
const ResearchPage = lazy(() => import("@/pages/public/ResearchPage"));
const CareersPage = lazy(() => import("@/pages/public/CareersPage"));
const PressPage = lazy(() => import("@/pages/public/PressPage"));
const ContactPage = lazy(() => import("@/pages/public/ContactPage"));
const FAQPage = lazy(() => import("@/pages/public/FAQPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/public/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/public/TermsPage"));
const HIPAAPage = lazy(() => import("@/pages/public/HIPAAPage"));
const CardSavedThankYou = lazy(() => import("@/pages/public/CardSavedThankYou"));
const ShopPage = lazy(() => import("@/pages/public/ShopPage"));
const ShopThankYou = lazy(() => import("@/pages/public/ShopThankYou"));
const AbandonedCheckouts = lazy(() => import("@/pages/admin/AbandonedCheckouts"));
const UnlinkedPayments = lazy(() => import("@/pages/admin/UnlinkedPayments"));
const LeadsPage = lazy(() => import("@/pages/admin/LeadsPage"));
const IVPage = lazy(() => import("@/pages/public/IVPage"));
const IVTrackingPage = lazy(() => import("@/pages/public/IVTrackingPage"));
const IVDispatch = lazy(() => import("@/pages/admin/IVDispatch"));
const NurseIV = lazy(() => import("@/pages/admin/NurseIV"));

function StaffRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageFallback />;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role === "patient") return <Navigate to="/portal" replace />;
  return children;
}

function PatientRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageFallback />;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "patient") return <Navigate to="/admin" replace />;
  return children;
}

function PublicPage({ children }) {
  return <PublicPageLayout>{children}</PublicPageLayout>;
}

// Suspense fallback used for lazy-loaded chunks. Quiet, non-jumpy —
// 200ms fade-in matches the page-fade-in animation in App.css.
function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] text-[#2A3324]/30 font-sans">
      Loading…
    </div>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />

        {/* Peptide Category Pages */}
        <Route path="/peptides/weight-management" element={<PublicPage><PeptideCategoryPage slug="weight-management" /></PublicPage>} />
        <Route path="/peptides/anti-aging" element={<PublicPage><PeptideCategoryPage slug="anti-aging" /></PublicPage>} />
        <Route path="/peptides/muscle-recovery" element={<PublicPage><PeptideCategoryPage slug="muscle-recovery" /></PublicPage>} />
        <Route path="/peptides/cognitive-health" element={<PublicPage><PeptideCategoryPage slug="cognitive-health" /></PublicPage>} />
        <Route path="/peptides/immune-support" element={<PublicPage><PeptideCategoryPage slug="immune-support" /></PublicPage>} />

        {/* Company Pages */}
        <Route path="/about" element={<PublicPage><AboutPage /></PublicPage>} />
        <Route path="/physicians" element={<PublicPage><PhysiciansPage /></PublicPage>} />
        <Route path="/research" element={<PublicPage><ResearchPage /></PublicPage>} />
        <Route path="/careers" element={<PublicPage><CareersPage /></PublicPage>} />
        <Route path="/press" element={<PublicPage><PressPage /></PublicPage>} />

        {/* Support Pages */}
        <Route path="/pricing" element={<PublicPage><PricingPage /></PublicPage>} />
        <Route path="/contact" element={<PublicPage><ContactPage /></PublicPage>} />
        <Route path="/faq" element={<PublicPage><FAQPage /></PublicPage>} />
        <Route path="/privacy" element={<PublicPage><PrivacyPolicyPage /></PublicPage>} />
        <Route path="/terms" element={<PublicPage><TermsPage /></PublicPage>} />
        <Route path="/hipaa" element={<PublicPage><HIPAAPage /></PublicPage>} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/card-saved" element={<CardSavedThankYou />} />
        <Route path="/intake/:token" element={<PublicIntakeForm />} />
        <Route path="/lp/get-started" element={<GetStartedLanding />} />
        <Route path="/shop/thank-you" element={<ShopThankYou />} />
        <Route path="/iv" element={<IVPage />} />
        <Route path="/iv/track/:orderId" element={<IVTrackingPage />} />
        <Route path="/signup" element={<PublicSignup />} />
        <Route path="/upload/:token" element={<PublicDocumentUpload />} />
        <Route
          path="/admin"
          element={<StaffRoute><AdminLayout /></StaffRoute>}
        >
          <Route index element={<DashboardHome />} />
          <Route path="medical-reviews" element={<PendingMedicalReviews />} />
          <Route path="profile/signature" element={<AdminSignaturePad />} />
          <Route path="profile/security" element={<AdminSecurity />} />
          <Route path="applicants" element={<ApplicantList />} />
          <Route path="applicants/:id" element={<ApplicantProfile />} />
          <Route path="paying-clients" element={<PayingClientsPage />} />
          <Route path="sms-automations" element={<SMSAutomationsPage />} />
          <Route path="fulfillment" element={<FulfillmentBoard />} />
          <Route path="packages" element={<PackageManager />} />
          <Route path="inventory" element={<InventoryPNL />} />
          <Route path="comms" element={<CommunicationsHub />} />
          <Route path="intake-templates" element={<IntakeTemplatesPage />} />
          <Route path="phone-numbers" element={<PhoneNumbersPage />} />
          <Route path="appointments" element={<AppointmentsPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="abandoned-checkouts" element={<AbandonedCheckouts />} />
          <Route path="unlinked-payments" element={<UnlinkedPayments />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="iv-dispatch" element={<IVDispatch />} />
          <Route path="nurse-iv" element={<NurseIV />} />
          <Route path="reports" element={<ReportingPage />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="labs" element={<LabTracker />} />
          <Route path="settings" element={<StatusSettings />} />
          <Route path="pricing-settings" element={<PricingSettings />} />
          <Route path="pipeline" element={<KanbanBoard />} />
          <Route path="content" element={<ContentHub />} />
          <Route path="content/:id" element={<ArticleEditor />} />
          <Route path="citations" element={<CitationLibrary />} />
        </Route>
        <Route
          path="/portal"
          element={<PatientRoute><PatientLayout /></PatientRoute>}
        >
          <Route index element={<PatientDashboard />} />
          <Route path="program" element={<PatientProgram />} />
          <Route path="manage-plan" element={<PatientManagePlan />} />
          <Route path="notifications" element={<PatientNotifications />} />
          <Route path="referrals" element={<PatientReferrals />} />
          <Route path="prescriptions" element={<PatientPrescriptions />} />
          <Route path="data-export" element={<PatientDataExport />} />
          <Route path="peptides" element={<PeptideBreakdown />} />
          <Route path="calendar" element={<PatientCalendar />} />
          <Route path="schedule" element={<PatientSchedule />} />
          <Route path="doses" element={<PatientDoses />} />
          <Route path="survey" element={<PatientSurvey />} />
          <Route path="results" element={<PatientResults />} />
          <Route path="notes" element={<PatientNotes />} />
          <Route path="labs" element={<PatientLabs />} />
          <Route path="messages" element={<PatientMessages />} />
          <Route path="call" element={<PatientCallRequest />} />
          <Route path="files" element={<PatientFiles />} />
          <Route path="urgent-call/success" element={<PatientUrgentCallSuccess />} />
          <Route path="settings" element={<PatientSettings />} />
        </Route>
        {/* 404 catch-all — must be last */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="bottom-right" />
      </ErrorBoundary>
    </div>
  );
}

export default App;
